import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  Button,
  Grid,
  Chip,
  Divider,
  Alert,
  CircularProgress,
  TextField,
  Snackbar
} from '@mui/material';
import {
  Person,
  Email,
  Phone,
  LocationOn,
  School,
  Work,
  CalendarToday,
  Edit,
  Save,
  Cancel
} from '@mui/icons-material';
import { useAppStore } from '../stores/useAppStore';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import { logger } from '../utils/logger';


export const UserProfilePage: React.FC = () => {
  const { user, setUser, updateUser } = useAppStore();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success'
  });

  const [editForm, setEditForm] = useState({
    fullName: '',
    email: '',
    phoneNumber: '',
    address: '',
    school: '',
    position: ''
  });

  // Fetch user info with dateCreated if not available
  useEffect(() => {
    const fetchUserInfo = async () => {
      if (user) {
        try {
          setLoading(true);
          const response = await apiService.getCurrentUser();
          if (response.success && response.user) {
            setUser(response.user);
            setEditForm({
              fullName: response.user.fullName || '',
              email: response.user.email || '',
              phoneNumber: response.user.phoneNumber || '',
              address: response.user.address || '',
              school: response.user.school || '',
              position: response.user.position || ''
            });
          }
        } catch (error) {
          logger.error('Error fetching user info:', error);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchUserInfo();
  }, [user?.id, setUser]); // Re-fetch when user ID changes

  if (!user) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  const handleEditProfile = () => {
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    if (user) {
      setEditForm({
        fullName: user.fullName || '',
        email: user.email || '',
        phoneNumber: user.phoneNumber || '',
        address: user.address || '',
        school: user.school || '',
        position: user.position || ''
      });
    }
  };

  const handleInputChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditForm(prev => ({ ...prev, [field]: e.target.value }));
  };

  const handleSaveProfile = async () => {
    if (!user) return;
    setSaving(true);
    try {
      const response = await apiService.put<{ success: boolean; user: any }>('/api/users/me', editForm);
      if (response.success && response.user) {
        updateUser(response.user);
        setIsEditing(false);
        setSnackbar({ open: true, message: 'Cập nhật thông tin thành công!', severity: 'success' });
      } else {
        throw new Error('Update failed');
      }
    } catch (error) {
      logger.error('Error updating profile:', error);
      setSnackbar({ open: true, message: 'Có lỗi xảy ra khi cập nhật thông tin.', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const getRoleColor = (role: string) => {
    switch (role.toLowerCase()) {
      case 'admin':
        return 'error';
      case 'approver':
        return 'secondary'; // Màu tím cho Ban Giám Hiệu
      case 'librarian':
        return 'primary';
      case 'teacher':
        return 'success';
      case 'student':
        return 'info';
      default:
        return 'default';
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role.toLowerCase()) {
      case 'admin':
        return 'Quản trị viên';
      case 'approver':
        return 'BGH'; // Ban Giám Hiệu
      case 'librarian':
        return 'Thủ thư';
      case 'teacher':
        return 'Giáo viên';
      case 'student':
        return 'Học sinh';
      default:
        return role;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Thông tin cá nhân
      </Typography>

      <Grid container spacing={3}>
        {/* Profile Card */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center', p: 3 }}>
              <Avatar
                sx={{
                  width: 120,
                  height: 120,
                  mx: 'auto',
                  mb: 2,
                  fontSize: '3rem',
                  bgcolor: 'primary.main'
                }}
              >
                {user.fullName?.charAt(0) || 'U'}
              </Avatar>

              <Typography variant="h5" gutterBottom>
                {user.fullName || 'Người dùng'}
              </Typography>

              <Typography variant="body2" color="text.secondary" gutterBottom>
                {user.email}
              </Typography>

              <Box sx={{ mt: 2, mb: 2 }}>
                {user.roles?.map((role, index) => {
                  const roleColor = getRoleColor(role);
                  const isApprover = role.toLowerCase() === 'approver';
                  return (
                    <Chip
                      key={index}
                      label={getRoleLabel(role)}
                      color={isApprover ? undefined : (roleColor as any)}
                      size="small"
                      sx={isApprover ? {
                        mr: 1,
                        mb: 1,
                        backgroundColor: '#9c27b0', // Màu tím
                        color: '#fff',
                        '&:hover': {
                          backgroundColor: '#7b1fa2',
                        }
                      } : { mr: 1, mb: 1 }}
                    />
                  );
                })}
              </Box>

              {!isEditing ? (
                <Button
                  variant="outlined"
                  startIcon={<Edit />}
                  onClick={handleEditProfile}
                  fullWidth
                >
                  Chỉnh sửa thông tin
                </Button>
              ) : (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="contained"
                    startIcon={<Save />}
                    onClick={handleSaveProfile}
                    fullWidth
                    disabled={saving}
                  >
                    {saving ? 'Đang lưu...' : 'Lưu'}
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<Cancel />}
                    onClick={handleCancelEdit}
                    fullWidth
                  >
                    Hủy
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Details Card */}
        <Grid size={{ xs: 12, md: 8 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Chi tiết thông tin
              </Typography>

              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Person sx={{ mr: 2, color: 'primary.main' }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Họ và tên
                      </Typography>
                      {isEditing ? (
                        <TextField
                          fullWidth
                          size="small"
                          name="fullName"
                          value={editForm.fullName}
                          onChange={handleInputChange('fullName')}
                          sx={{ mt: 0.5 }}
                        />
                      ) : (
                        <Typography variant="body1">
                          {user.fullName || 'Chưa cập nhật'}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Email sx={{ mr: 2, color: 'primary.main' }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Email
                      </Typography>
                      {isEditing ? (
                        <TextField
                          fullWidth
                          size="small"
                          name="email"
                          value={editForm.email}
                          onChange={handleInputChange('email')}
                          sx={{ mt: 0.5 }}
                        />
                      ) : (
                        <Typography variant="body1">
                          {user.email || 'Chưa cập nhật'}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Phone sx={{ mr: 2, color: 'primary.main' }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Số điện thoại
                      </Typography>
                      {isEditing ? (
                        <TextField
                          fullWidth
                          size="small"
                          name="phoneNumber"
                          value={editForm.phoneNumber}
                          onChange={handleInputChange('phoneNumber')}
                          sx={{ mt: 0.5 }}
                        />
                      ) : (
                        <Typography variant="body1">
                          {user.phoneNumber || 'Chưa cập nhật'}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <LocationOn sx={{ mr: 2, color: 'primary.main' }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Địa chỉ
                      </Typography>
                      {isEditing ? (
                        <TextField
                          fullWidth
                          size="small"
                          name="address"
                          value={editForm.address}
                          onChange={handleInputChange('address')}
                          multiline
                          rows={2}
                          sx={{ mt: 0.5 }}
                        />
                      ) : (
                        <Typography variant="body1">
                          {user.address || 'Chưa cập nhật'}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <School sx={{ mr: 2, color: 'primary.main' }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Trường học
                      </Typography>
                      {isEditing ? (
                        <TextField
                          fullWidth
                          size="small"
                          name="school"
                          value={editForm.school}
                          onChange={handleInputChange('school')}
                          sx={{ mt: 0.5 }}
                        />
                      ) : (
                        <Typography variant="body1">
                          {user.school || 'Chưa cập nhật'}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Work sx={{ mr: 2, color: 'primary.main' }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Chức vụ
                      </Typography>
                      {isEditing ? (
                        <TextField
                          fullWidth
                          size="small"
                          name="position"
                          value={editForm.position}
                          onChange={handleInputChange('position')}
                          sx={{ mt: 0.5 }}
                        />
                      ) : (
                        <Typography variant="body1">
                          {user.position || 'Chưa cập nhật'}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <CalendarToday sx={{ mr: 2, color: 'primary.main' }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Ngày tạo tài khoản
                      </Typography>
                      <Typography variant="body1">
                        {user.dateCreated ? new Date(user.dateCreated).toLocaleDateString('vi-VN') : 'Chưa có thông tin'}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>

              {(!user.phoneNumber || !user.address) && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Một số thông tin cá nhân chưa được cập nhật.
                  <Button size="small" onClick={handleEditProfile} sx={{ ml: 1 }}>
                    Cập nhật ngay
                  </Button>
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default UserProfilePage;
